import { useCallback, useEffect, useRef, useState } from "react";

const CANDIDATES = [
  "audio/webm;codecs=opus",
  "audio/webm",
  "audio/mp4;codecs=mp4a.40.2",
  "audio/mp4",
  "audio/aac",
  "audio/ogg;codecs=opus",
];
const MAX_MS = 3 * 60 * 1000;

export const pickMimeType = () => {
  if (typeof window === "undefined" || !window.MediaRecorder) return null;
  return CANDIDATES.find((m) => MediaRecorder.isTypeSupported?.(m)) || "";
};

export const extForMime = (mime) => {
  const base = (mime || "").split(";")[0];
  return { "audio/webm": "webm", "audio/mp4": "mp4", "audio/aac": "aac", "audio/ogg": "ogg" }[base] || "webm";
};

export function useRecorder() {
  const [state, setState] = useState("idle");
  const [elapsed, setElapsed] = useState(0);
  const [error, setError] = useState(null);
  const [mimeType] = useState(pickMimeType);
  const recRef = useRef(null);
  const chunks = useRef([]);
  const timer = useRef(null);
  const resolver = useRef(null);

  const cleanup = () => {
    clearInterval(timer.current);
    recRef.current?.stream?.getTracks().forEach((t) => t.stop());
  };

  useEffect(() => cleanup, []);

  const start = useCallback(async () => {
    setError(null);
    if (mimeType === null) {
      setError("Este navegador no soporta grabación (MediaRecorder).");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      chunks.current = [];
      rec.ondataavailable = (e) => e.data.size && chunks.current.push(e.data);
      rec.onstop = () => {
        cleanup();
        const type = rec.mimeType || mimeType || "audio/webm";
        const blob = new Blob(chunks.current, { type });
        setState("idle");
        resolver.current?.({ blob, mimeType: type, ext: extForMime(type) });
      };
      recRef.current = rec;
      rec.start(1000);
      const t0 = Date.now();
      setElapsed(0);
      timer.current = setInterval(() => {
        const ms = Date.now() - t0;
        setElapsed(ms);
        if (ms >= MAX_MS && rec.state === "recording") rec.stop();
      }, 250);
      setState("recording");
    } catch (e) {
      setError(e.name === "NotAllowedError" ? "Permiso de micrófono denegado." : `No se pudo iniciar: ${e.message}`);
      setState("idle");
    }
  }, [mimeType]);

  const stop = useCallback(
    () =>
      new Promise((resolve) => {
        resolver.current = resolve;
        if (recRef.current?.state === "recording") recRef.current.stop();
        else resolve(null);
      }),
    []
  );

  return { state, elapsed, error, mimeType, start, stop };
}
